import { useEffect, useRef, useState } from 'react'

/**
 * 差分视图：把处理前后逐像素比一遍，只把动过的地方点亮。
 *
 * 背景：2026-09-11 用户裁决——「这里好像看得很不明显啊」。
 *   切格子那一档是无损切除，实测最大单通道差 2 个色阶（见 PixelDiffNote），
 *   对比板两侧再怎么拖分割线都是一样的。数值能证明动过，但数值证明不了「动在哪」，
 *   于是页面上写着「已处理」，眼睛里什么都没有。
 * 设计意图：算出来的差值本身就是被切掉的那层网格——把它按实测最大值归一化后铺开，
 *   用户看到的是一张规则的网纹，正是诊断里说的那层东西。这比任何一句解释都直接。
 * 关键约束：
 *   1. 只在前后尺寸一致时画。走过深度修复的成品是重画的，尺寸和内容都变了，
 *      逐像素相减出来的是两张不同的画，不是「改动量」，那会误导人。
 *   2. 放大倍数写在图注里。差值被放大了几十倍才看得见，不说清楚，
 *      这张图看着像「画面被改得面目全非」，与我们「无损」的说法自相矛盾。
 *   3. 取不到像素（跨域、解码失败）就整块不渲染，不留空壳也不报错——
 *      它是佐证，不是结论，结论在读数里。
 *
 * 2026-09-11 用户裁决：「除了有叠加的，还有原图，还有差分图」——这张图原先是对比板
 *   下面另起的一块，现在并进对比板，成为它的一档视图（放大镜 / 分割线 / 只看某侧 / 差分）。
 *   同一幅画的几种看法摆在同一个槽里切换，用户不必在两块图之间来回找对应位置。
 *   于是这里只出画布，图注与实测值由对比板那一行统一说（CaseCompare）。
 */
export function DiffView({ beforeSrc, afterSrc, onStats }: {
  beforeSrc: string
  afterSrc: string
  /** 算完了报一次：最大差了几个色阶、点亮时放大了多少倍。画不出来报 null。 */
  onStats?: (s: { levels: number; gain: number } | null) => void
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  const [failed, setFailed] = useState(false)
  const report = useRef(onStats)
  report.current = onStats

  useEffect(() => {
    let alive = true
    setFailed(false)

    const load = (src: string) => new Promise<HTMLImageElement>((res, rej) => {
      const im = new Image()
      im.onload = () => res(im)
      im.onerror = () => rej(new Error(src))
      im.src = src
    })

    Promise.all([load(beforeSrc), load(afterSrc)]).then(([a, b]) => {
      if (!alive) return
      const w = a.naturalWidth
      const h = a.naturalHeight
      // 约束 1：尺寸不一致就不是同一幅画的两次采样
      if (!w || !h || b.naturalWidth !== w || b.naturalHeight !== h) throw new Error('size')

      const grab = (im: HTMLImageElement) => {
        const c = document.createElement('canvas')
        c.width = w
        c.height = h
        const g = c.getContext('2d', { willReadFrequently: true })!
        g.drawImage(im, 0, 0)
        return g.getImageData(0, 0, w, h).data
      }
      const pa = grab(a)
      const pb = grab(b)

      // 差值本身只有 1 到 2 个色阶，而且是散点。原样铺出来再交给浏览器缩小，
      // 缩小就是求平均，一平均就回到全黑——正是「看不明显」那个毛病本身。
      // 所以先按块取最大值降采样到接近显示尺寸，再把动过的地方点亮：
      // 这张图要回答的是「动在哪」，不是「动了多少」（多少由读数那一行给）。
      const block = Math.max(1, Math.ceil(Math.max(w, h) / 1100))
      const ow = Math.ceil(w / block)
      const oh = Math.ceil(h / block)
      const pooled = new Uint8Array(ow * oh)
      let maxLevels = 0
      for (let y = 0, p = 0; y < h; y++) {
        const oy = ((y / block) | 0) * ow
        for (let x = 0; x < w; x++, p += 4) {
          const v = Math.max(
            Math.abs(pa[p] - pb[p]),
            Math.abs(pa[p + 1] - pb[p + 1]),
            Math.abs(pa[p + 2] - pb[p + 2]),
          )
          if (v > maxLevels) maxLevels = v
          const i = oy + ((x / block) | 0)
          if (v > pooled[i]) pooled[i] = v
        }
      }
      if (!maxLevels) throw new Error('identical')
      // 差到这个地步的就不是同一幅画的两次采样了（比如成品被重新压过 JPEG，
      // 或者两张图根本来自不同的运行）。那种情况下点亮出来的是整幅画的鬼影，
      // 与我们「无损、画面没动」的说法互相打脸，宁可不画。
      if (maxLevels > 24) throw new Error('not lossless')

      // 定标用分位数，不用最大值：一个饱和边缘上的孤点就能把最大值顶到 255，
      // 那时整张网纹被压成全黑，正好把要给人看的东西看没了。
      const hist = new Uint32Array(256)
      let nonzero = 0
      for (let i = 0; i < pooled.length; i++) {
        if (pooled[i]) { hist[pooled[i]]++; nonzero++ }
      }
      let acc = 0
      let cut = 1
      for (let v = 1; v < 256; v++) {
        acc += hist[v]
        if (acc >= nonzero * 0.995) { cut = v; break }
      }

      // 怎么定标，看这次动过的地方有多密。切格子那一档实测有两种：
      //
      //   · 稀疏：只动了散落的几处（早期那次实测 0.05% 的像素）。按幅度画的话，
      //     孤零零的点太暗，看着像什么都没发生——所以动过就给一个底亮度，
      //     差 1 个色阶和差 2 个色阶都要看得见，它们的区别不重要，「哪儿动过」才重要。
      //   · 密集：几乎每个像素都被动了一两个色阶（网格本来就铺满全幅）。
      //     这时底亮度是灾难：每个像素都踩到 110，整幅糊成一块平板（实测：一片沙色），
      //     既说不出「动在哪」，也说不出「动了多少」。改成按幅度定标——
      //     密的那种，幅度的起伏本身就是被切掉的那层网格的形状。
      //     定标压到最大幅度的一半（顶上那一半直接烧到满亮），指数给到 1.2 把
      //     差一个色阶的那些压暗，纹路才从底噪里浮出来。
      const dense = nonzero > pooled.length * 0.2
      const scale = dense ? Math.max(1, Math.round(maxLevels / 2)) : cut
      const gamma = dense ? 1.2 : 0.6
      const floor = dense ? 0 : 110
      const span = 255 - floor

      const out = new ImageData(ow, oh)
      for (let i = 0, p = 0; i < pooled.length; i++, p += 4) {
        const v = pooled[i]
        const lum = v ? Math.min(255, floor + span * Math.pow(Math.min(v, scale) / scale, gamma)) : 0
        // 暖白：底色是黑的，动过的地方越亮越暖，与页面上「红色是标记」那套不冲突
        out.data[p] = lum
        out.data[p + 1] = lum * 0.86
        out.data[p + 2] = lum * 0.62
        out.data[p + 3] = 255
      }
      const cv = ref.current
      if (!cv) return
      cv.width = ow
      cv.height = oh
      cv.getContext('2d')!.putImageData(out, 0, 0)
      // 放大了多少倍：定标位（cut）被映射到满亮度，倍数就是 255 除以它。
      // 这个数要写在图上，否则这张图看着像「画面被改得面目全非」。
      report.current?.({ levels: maxLevels, gain: Math.max(1, Math.round(255 / scale)) })
    }).catch(() => {
      if (!alive) return
      setFailed(true)
      report.current?.(null)
    })

    return () => { alive = false }
  }, [beforeSrc, afterSrc])

  if (failed) return null

  return <canvas className="cmp-diff" ref={ref} aria-label="处理前后的差分图，亮的地方是被改动过的像素" />
}
